import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { sucursalesAPI, productosAPI, getImageUrl } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const Transferencias = () => {
  const { formatCurrency } = useApp();
  const { sucursales, sucursalActiva, esAdmin, modoEspectador } = useAuth();
  const [transferencias, setTransferencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [showNueva, setShowNueva] = useState(false);
  const [showDetalle, setShowDetalle] = useState(null);
  const [showRechazar, setShowRechazar] = useState(null);
  const [procesando, setProcesando] = useState(false);

  // Form nueva transferencia
  const [sucursalDestino, setSucursalDestino] = useState('');
  const [notas, setNotas] = useState('');
  const [productosTransf, setProductosTransf] = useState([]);
  const [buscarProd, setBuscarProd] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');

  // Cargar transferencias
  const cargarTransferencias = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (sucursalActiva) params.sucursal_id = sucursalActiva;
      const res = await sucursalesAPI.getTransferencias(params);
      if (res.data.success) setTransferencias(res.data.data);
    } catch { toast.error('Error al cargar transferencias'); }
    finally { setLoading(false); }
  }, [filtroEstado, sucursalActiva]);

  useEffect(() => { cargarTransferencias(); }, [cargarTransferencias]);

  // Buscar productos para la transferencia
  const buscarProductos = async () => {
    if (!buscarProd.trim()) return;
    setBuscando(true);
    try {
      const res = await sucursalesAPI.getStock(sucursalActiva || 1, { buscar: buscarProd });
      if (res.data.success) setResultadosBusqueda(res.data.data);
    } catch { toast.error('Error al buscar productos'); }
    finally { setBuscando(false); }
  };

  // Agregar producto a la transferencia
  const agregarProducto = (prod) => {
    if (productosTransf.find(p => p.producto_id === prod.producto_id)) {
      toast.warning('Producto ya agregado');
      return;
    }
    setProductosTransf(prev => [...prev, {
      producto_id: prod.producto_id,
      nombre: prod.producto_nombre,
      codigo: prod.producto_codigo,
      imagen_url: prod.imagen_url,
      marca: prod.marca,
      categoria: prod.categoria,
      stock_disponible: prod.stock_actual,
      cantidad: 1
    }]);
    setResultadosBusqueda([]);
    setBuscarProd('');
  };

  // Cambiar cantidad
  const cambiarCantidad = (idx, cant) => {
    setProductosTransf(prev => prev.map((p, i) =>
      i === idx ? { ...p, cantidad: Math.max(1, Math.min(cant, p.stock_disponible)) } : p
    ));
  };

  // Quitar producto
  const quitarProducto = (idx) => {
    setProductosTransf(prev => prev.filter((_, i) => i !== idx));
  };

  // Crear transferencia
  const crearTransferencia = async () => {
    if (!sucursalDestino) { toast.error('Seleccione sucursal destino'); return; }
    if (productosTransf.length === 0) { toast.error('Agregue al menos un producto'); return; }

    setProcesando(true);
    try {
      const data = {
        sucursal_origen_id: parseInt(sucursalActiva),
        sucursal_destino_id: parseInt(sucursalDestino),
        notas,
        productos: productosTransf.map(p => ({ producto_id: p.producto_id, cantidad: p.cantidad }))
      };
      const res = await sucursalesAPI.crearTransferencia(data);
      if (res.data.success) {
        toast.success(`Transferencia ${res.data.data.codigo} creada`);
        setShowNueva(false);
        resetForm();
        cargarTransferencias();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear transferencia');
    } finally { setProcesando(false); }
  };

  const resetForm = () => {
    setSucursalDestino('');
    setNotas('');
    setProductosTransf([]);
    setBuscarProd('');
    setResultadosBusqueda([]);
  };

  // Ver detalle
  const verDetalle = async (id) => {
    try {
      const res = await sucursalesAPI.getTransferenciaDetalle(id);
      if (res.data.success) setShowDetalle(res.data.data);
    } catch { toast.error('Error al cargar detalle'); }
  };

  // Acciones sobre transferencia
  const aceptarTransferencia = async (id) => {
    if (!window.confirm('Confirmar recepcion de todos los productos?')) return;
    setProcesando(true);
    try {
      const res = await sucursalesAPI.completarTransferencia(id);
      if (res.data.success) {
        toast.success('Transferencia aceptada y completada');
        setShowDetalle(null);
        cargarTransferencias();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al completar');
    } finally { setProcesando(false); }
  };

  const rechazarTransferencia = async () => {
    if (!motivoRechazo.trim()) { toast.error('Indique el motivo del rechazo'); return; }
    setProcesando(true);
    try {
      const res = await sucursalesAPI.rechazarTransferencia(showRechazar, { motivo_rechazo: motivoRechazo });
      if (res.data.success) {
        toast.success('Transferencia rechazada');
        setShowRechazar(null);
        setMotivoRechazo('');
        setShowDetalle(null);
        cargarTransferencias();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al rechazar');
    } finally { setProcesando(false); }
  };

  const cancelarTransf = async (id) => {
    if (!window.confirm('Cancelar esta transferencia?')) return;
    setProcesando(true);
    try {
      const res = await sucursalesAPI.cancelarTransferencia(id);
      if (res.data.success) {
        toast.success('Transferencia cancelada');
        setShowDetalle(null);
        cargarTransferencias();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cancelar');
    } finally { setProcesando(false); }
  };

  const enviarTransito = async (id) => {
    setProcesando(true);
    try {
      const res = await sucursalesAPI.marcarEnTransito(id);
      if (res.data.success) {
        toast.success('Transferencia marcada como en transito');
        setShowDetalle(null);
        cargarTransferencias();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    } finally { setProcesando(false); }
  };

  // Badge estado
  const estadoBadge = (estado) => {
    const colors = {
      pendiente: 'warning', en_transito: 'info', completada: 'success',
      cancelada: 'secondary', rechazada: 'danger'
    };
    const labels = {
      pendiente: 'Pendiente', en_transito: 'En Transito', completada: 'Completada',
      cancelada: 'Cancelada', rechazada: 'Rechazada'
    };
    return <span className={`badge bg-${colors[estado] || 'secondary'}`}>{labels[estado] || estado}</span>;
  };

  const formatFecha = (f) => f ? new Date(f).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  // Determinar si soy la sucursal destino (para aceptar/rechazar)
  const esSucursalDestino = (trans) => String(trans.sucursal_destino_id) === String(sucursalActiva);
  const esSucursalOrigen = (trans) => String(trans.sucursal_origen_id) === String(sucursalActiva);

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2 align-items-center">
          <select className="form-select form-select-sm" style={{ width: '160px' }}
            value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="en_transito">En Transito</option>
            <option value="completada">Completada</option>
            <option value="rechazada">Rechazada</option>
            <option value="cancelada">Cancelada</option>
          </select>
          <button className="btn btn-sm btn-outline-secondary" onClick={cargarTransferencias}>
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setShowNueva(true); }} disabled={modoEspectador}>
          <i className="bi bi-plus-lg me-1"></i>Nueva Transferencia
        </button>
      </div>

      {/* Tabla */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Codigo</th>
                <th>Fecha</th>
                <th>Origen</th>
                <th>Destino</th>
                <th>Solicitado por</th>
                <th className="text-center">Estado</th>
                <th className="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-4"><div className="spinner-border text-primary"></div></td></tr>
              ) : transferencias.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-4 text-muted">
                  <i className="bi bi-inbox fs-1 d-block mb-2"></i>No hay transferencias
                </td></tr>
              ) : transferencias.map(t => (
                <tr key={t.id} className={esSucursalDestino(t) && t.estado === 'pendiente' ? 'table-warning' : ''}>
                  <td className="font-monospace fw-bold small">{t.codigo}</td>
                  <td className="small">{formatFecha(t.created_at)}</td>
                  <td>
                    <span className="badge bg-primary bg-opacity-10 text-primary">{t.sucursal_origen || t.codigo_origen}</span>
                  </td>
                  <td>
                    <span className="badge bg-info bg-opacity-10 text-info">{t.sucursal_destino || t.codigo_destino}</span>
                  </td>
                  <td className="small">{t.solicitado_por || '-'}</td>
                  <td className="text-center">{estadoBadge(t.estado)}</td>
                  <td className="text-center">
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-outline-secondary" onClick={() => verDetalle(t.id)} title="Ver detalle">
                        <i className="bi bi-eye"></i>
                      </button>
                      {/* Sucursal destino puede aceptar/rechazar */}
                      {esSucursalDestino(t) && (t.estado === 'pendiente' || t.estado === 'en_transito') && (
                        <>
                          <button className="btn btn-outline-success" onClick={() => aceptarTransferencia(t.id)} title="Aceptar y recibir" disabled={procesando}>
                            <i className="bi bi-check-lg"></i>
                          </button>
                          <button className="btn btn-outline-danger" onClick={() => { setShowRechazar(t.id); setMotivoRechazo(''); }} title="Rechazar" disabled={procesando}>
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </>
                      )}
                      {/* Sucursal origen puede cancelar o enviar */}
                      {(esSucursalOrigen(t) || esAdmin()) && t.estado === 'pendiente' && (
                        <>
                          <button className="btn btn-outline-info" onClick={() => enviarTransito(t.id)} title="Marcar en transito" disabled={procesando}>
                            <i className="bi bi-truck"></i>
                          </button>
                          <button className="btn btn-outline-warning" onClick={() => cancelarTransf(t.id)} title="Cancelar" disabled={procesando}>
                            <i className="bi bi-x-circle"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Nueva Transferencia */}
      {showNueva && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowNueva(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title"><i className="bi bi-arrow-left-right me-2"></i>Nueva Transferencia</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowNueva(false)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Sucursal Origen</label>
                      <input type="text" className="form-control" disabled
                        value={sucursales.find(s => String(s.id) === String(sucursalActiva))?.nombre || 'Sucursal actual'} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Sucursal Destino</label>
                      <select className="form-select" value={sucursalDestino} onChange={(e) => setSucursalDestino(e.target.value)}>
                        <option value="">-- Seleccionar --</option>
                        {sucursales.filter(s => String(s.id) !== String(sucursalActiva) && s.activo !== false).map(s => (
                          <option key={s.id} value={s.id}>{s.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notas (opcional)</label>
                      <input type="text" className="form-control" value={notas} onChange={(e) => setNotas(e.target.value)}
                        placeholder="Observaciones de la transferencia" />
                    </div>
                  </div>

                  {/* Buscar productos */}
                  <div className="card">
                    <div className="card-header py-2 d-flex gap-2">
                      <input type="text" className="form-control form-control-sm" placeholder="Buscar producto por nombre o codigo..."
                        value={buscarProd} onChange={(e) => setBuscarProd(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && buscarProductos()} />
                      <button className="btn btn-sm btn-primary" onClick={buscarProductos} disabled={buscando}>
                        {buscando ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-search"></i>}
                      </button>
                    </div>

                    {/* Resultados busqueda */}
                    {resultadosBusqueda.length > 0 && (
                      <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflow: 'auto' }}>
                        {resultadosBusqueda.map(p => (
                          <button key={p.producto_id} className="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2"
                            onClick={() => agregarProducto(p)}>
                            <div style={{ width: 45, height: 45, flexShrink: 0 }}>
                              {p.imagen_url ? (
                                <img src={getImageUrl(p.imagen_url)} alt="" style={{ width: 45, height: 45, objectFit: 'cover', borderRadius: 4 }}
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                              ) : null}
                              <div className="bg-light d-flex align-items-center justify-content-center rounded"
                                style={{ width: 45, height: 45, display: p.imagen_url ? 'none' : 'flex' }}>
                                <i className="bi bi-box text-muted"></i>
                              </div>
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <div className="small fw-semibold text-truncate">{p.producto_nombre}</div>
                              <div className="text-muted small">{p.producto_codigo}</div>
                              <div className="d-flex gap-2 small">
                                {p.marca && <span className="badge bg-light text-dark">{p.marca}</span>}
                                {p.categoria && <span className="badge bg-light text-dark">{p.categoria}</span>}
                                {p.tipo_producto && <span className="badge bg-info bg-opacity-10 text-info">{p.tipo_producto}</span>}
                              </div>
                            </div>
                            <div className="text-end" style={{ flexShrink: 0 }}>
                              <div className={`fw-semibold small ${p.stock_actual <= (p.stock_minimo || 2) ? 'text-danger' : 'text-success'}`}>
                                Stock: {p.stock_actual}
                              </div>
                              <div className="text-muted small">{formatCurrency(p.precio_venta)}</div>
                            </div>
                            <i className="bi bi-plus-circle-fill text-primary fs-5" style={{ flexShrink: 0 }}></i>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Productos seleccionados */}
                    <div className="card-body p-0">
                      {productosTransf.length === 0 ? (
                        <div className="text-center py-3 text-muted small">
                          Busque y agregue productos para transferir
                        </div>
                      ) : (
                        <table className="table table-sm mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Producto</th>
                              <th className="text-center" style={{ width: '100px' }}>Stock Disp.</th>
                              <th className="text-center" style={{ width: '120px' }}>Cantidad</th>
                              <th style={{ width: '40px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {productosTransf.map((p, idx) => (
                              <tr key={idx}>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    {p.imagen_url ? (
                                      <img src={getImageUrl(p.imagen_url)} alt="" style={{ width: 35, height: 35, objectFit: 'cover', borderRadius: 4 }}
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                    ) : (
                                      <div className="bg-light d-flex align-items-center justify-content-center rounded" style={{ width: 35, height: 35 }}>
                                        <i className="bi bi-box text-muted small"></i>
                                      </div>
                                    )}
                                    <div>
                                      <div className="small fw-medium">{p.nombre}</div>
                                      <div className="text-muted small">{p.codigo} {p.marca && `| ${p.marca}`}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-center">{p.stock_disponible}</td>
                                <td className="text-center">
                                  <input type="number" className="form-control form-control-sm text-center"
                                    value={p.cantidad} min={1} max={p.stock_disponible}
                                    onChange={(e) => cambiarCantidad(idx, parseInt(e.target.value) || 1)} />
                                </td>
                                <td>
                                  <button className="btn btn-sm btn-outline-danger" onClick={() => quitarProducto(idx)}>
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowNueva(false)}>Cancelar</button>
                  <button className="btn btn-primary" onClick={crearTransferencia} disabled={procesando || productosTransf.length === 0 || !sucursalDestino}>
                    {procesando ? <><span className="spinner-border spinner-border-sm me-1"></span>Creando...</> :
                      <><i className="bi bi-send me-1"></i>Crear Transferencia</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal Detalle */}
      {showDetalle && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowDetalle(null)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-light">
                  <h5 className="modal-title">
                    <i className="bi bi-arrow-left-right me-2"></i>
                    Transferencia {showDetalle.codigo}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowDetalle(null)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <div className="small text-muted">Estado</div>
                      <div>{estadoBadge(showDetalle.estado)}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="small text-muted">Fecha Creacion</div>
                      <div className="small">{formatFecha(showDetalle.created_at)}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="small text-muted">Fecha Recepcion</div>
                      <div className="small">{formatFecha(showDetalle.fecha_recepcion)}</div>
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <div className="card border-primary">
                        <div className="card-body py-2">
                          <div className="small text-muted">Origen</div>
                          <div className="fw-bold">{showDetalle.sucursal_origen}</div>
                          <div className="small text-muted">Solicitado por: {showDetalle.solicitado_por || '-'}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-info">
                        <div className="card-body py-2">
                          <div className="small text-muted">Destino</div>
                          <div className="fw-bold">{showDetalle.sucursal_destino}</div>
                          <div className="small text-muted">Aprobado por: {showDetalle.aprobado_por || '-'}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {showDetalle.notas && (
                    <div className="alert alert-light small mb-3">
                      <i className="bi bi-chat-text me-1"></i><strong>Notas:</strong> {showDetalle.notas}
                    </div>
                  )}

                  {/* Productos */}
                  <div className="card">
                    <div className="card-header py-2 small fw-bold">
                      <i className="bi bi-box-seam me-1"></i>Productos ({showDetalle.productos?.length || 0})
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Codigo</th>
                            <th>Producto</th>
                            <th className="text-center">Enviado</th>
                            <th className="text-center">Recibido</th>
                            <th className="text-end">Valor Unit.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(showDetalle.productos || []).map((p, idx) => (
                            <tr key={idx}>
                              <td className="font-monospace small">{p.producto_codigo}</td>
                              <td className="small">{p.producto_nombre}</td>
                              <td className="text-center">{p.cantidad}</td>
                              <td className="text-center">
                                {showDetalle.estado === 'completada' ? p.cantidad_recibida : '-'}
                              </td>
                              <td className="text-end small">{formatCurrency(p.precio_venta)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  {/* Acciones segun estado y sucursal */}
                  {esSucursalDestino(showDetalle) && (showDetalle.estado === 'pendiente' || showDetalle.estado === 'en_transito') && (
                    <>
                      <button className="btn btn-success btn-sm" onClick={() => aceptarTransferencia(showDetalle.id)} disabled={procesando}>
                        <i className="bi bi-check-lg me-1"></i>Aceptar y Recibir
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => { setShowRechazar(showDetalle.id); setMotivoRechazo(''); }} disabled={procesando}>
                        <i className="bi bi-x-lg me-1"></i>Rechazar
                      </button>
                    </>
                  )}
                  {(esSucursalOrigen(showDetalle) || esAdmin()) && showDetalle.estado === 'pendiente' && (
                    <>
                      <button className="btn btn-info btn-sm" onClick={() => enviarTransito(showDetalle.id)} disabled={procesando}>
                        <i className="bi bi-truck me-1"></i>Enviar (En Transito)
                      </button>
                      <button className="btn btn-warning btn-sm" onClick={() => cancelarTransf(showDetalle.id)} disabled={procesando}>
                        <i className="bi bi-x-circle me-1"></i>Cancelar
                      </button>
                    </>
                  )}
                  <button className="btn btn-light btn-sm" onClick={() => setShowDetalle(null)}>Cerrar</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal Rechazar */}
      {showRechazar && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={() => setShowRechazar(null)} />
          <div className="modal fade show d-block" style={{ zIndex: 1061 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title"><i className="bi bi-x-circle me-2"></i>Rechazar Transferencia</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowRechazar(null)} />
                </div>
                <div className="modal-body">
                  <label className="form-label fw-semibold">Motivo del rechazo</label>
                  <textarea className="form-control" rows="3" value={motivoRechazo}
                    onChange={(e) => setMotivoRechazo(e.target.value)}
                    placeholder="Indique por que rechaza esta transferencia..." />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowRechazar(null)}>Cancelar</button>
                  <button className="btn btn-danger" onClick={rechazarTransferencia} disabled={procesando || !motivoRechazo.trim()}>
                    {procesando ? <><span className="spinner-border spinner-border-sm me-1"></span>Rechazando...</> :
                      <><i className="bi bi-x-lg me-1"></i>Confirmar Rechazo</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Transferencias;
